/** @jsxImportSource @emotion/react */
import { Link, Typography } from '@mui/material'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * A lightweight markdown renderer for plain content areas.
 */

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Typography variant="subtitle1" component="h1" fontWeight={700} gutterBottom>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle2" component="h2" fontWeight={700} gutterBottom>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" component="h3" fontWeight={600} gutterBottom>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" paragraph sx={{ lineHeight: 1.7, mb: 1 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: '1.25rem', margin: '4px 0 8px' }} className="list-disc">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: '1.25rem', margin: '4px 0 8px' }} className="list-decimal">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: 2 }}>
      <Typography variant="body2" component="span">
        {children}
      </Typography>
    </li>
  ),
  strong: ({ children }) => (
    <Typography component="strong" variant="body2" fontWeight={700}>
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography component="em" variant="body2" sx={{ fontStyle: 'italic' }}>
      {children}
    </Typography>
  ),
  a: ({ children, href }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  hr: () => <div />,
  blockquote: ({ children }) => (
    <Typography
      component="blockquote"
      variant="body2"
      sx={{
        borderLeft: '3px solid',
        borderColor: 'divider',
        pl: 1.5,
        ml: 0,
        color: 'text.secondary',
        fontStyle: 'italic',
      }}
    >
      {children}
    </Typography>
  ),
  table: ({ children }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', margin: '8px 0' }}>{children}</table>
  ),
  th: ({ children }) => (
    <th
      style={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        padding: '4px 8px',
        textAlign: 'left',
      }}
    >
      <Typography variant="body2" fontWeight={700}>
        {children}
      </Typography>
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        borderBottom: '1px solid #999',
        borderColor: 'divider',
        padding: '4px 8px',
      }}
    >
      <Typography variant="body2">{children}</Typography>
    </td>
  ),
}

type SimpleMarkdownRendererProps = {
  markdown: string
}

export const SimpleMarkdownRenderer = ({ markdown }: SimpleMarkdownRendererProps) => {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {markdown}
    </ReactMarkdown>
  )
}
